import {
  type Account,
  type AlpacaCredentialSummary,
  type CreateCredentialRequest,
  type CreateNotifierRequest,
  type CredentialCheck,
  type CredentialListing,
  type CredentialSummary,
  discordWebhookId,
  type FeedStatus,
  type Notifier,
  type PolygonCredentialSummary,
  type TestCredentialKeysRequest,
  type UpdateCredentialRequest,
  type UpdateNotifierRequest,
} from '@fleece/models';

/**
 * The settings page's logic, apart from React so it can be tested as plain functions:
 * how a feed reads, which key serves what, and how a form's draft becomes a request.
 */

export type Tone = 'positive' | 'warning' | 'negative' | 'muted';

export interface FeedDisplay {
  readonly tone: Tone;
  readonly label: string;
  /** Why, when there is something to say. */
  readonly detail?: string;
}

/** How an Alpaca account's order feed reads at a glance. */
export function feedDisplay(feed: FeedStatus | undefined): FeedDisplay {
  switch (feed?.state) {
    case 'connected':
      return { tone: 'positive', label: 'Following orders' };
    case 'connecting':
      return { tone: 'warning', label: 'Connecting' };
    case 'reconnecting':
      return { tone: 'warning', label: 'Reconnecting', detail: feed.error };
    case 'failed':
      return { tone: 'negative', label: 'Not following', detail: feed.error };
    case 'off':
    case undefined:
      return { tone: 'muted', label: 'Not tracking orders' };
  }
}

/** How long ago, in as few words as still say it. */
export function ago(timestamp: number, now: number): string {
  const minutes = Math.floor((now - timestamp) / 60_000);
  if (minutes < 1) {
    return 'just now';
  }
  if (minutes < 60) {
    return `${minutes} min ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} h ago`;
  }
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

/** Whether the provider accepts a credential's keys, as it last said. */
export function keyStatus(credential: CredentialSummary, now: number): FeedDisplay {
  if (credential.invalid !== undefined) {
    return { tone: 'negative', label: 'Invalid', detail: `Refused since ${ago(credential.invalid.since, now)}. ${credential.invalid.reason}` };
  }
  if (credential.checkedAt !== undefined) {
    return { tone: 'positive', label: 'Valid', detail: `Checked ${ago(credential.checkedAt, now)}` };
  }
  return { tone: 'muted', label: 'Not tested', detail: 'Test to check them' };
}

export interface CheckDisplay {
  readonly tone: 'positive' | 'negative';
  readonly message: string;
}

/**
 * A provider's answer to a Test, in words. `accountId` is the account a credential being
 * edited is for: keys that answer for another would be refused on save, and saying so now
 * saves finding out then.
 */
export function checkDisplay(check: CredentialCheck, accountId?: string): CheckDisplay {
  if (!check.valid) {
    return { tone: 'negative', message: check.reason };
  }
  if (check.kind === 'polygon') {
    return { tone: 'positive', message: 'Polygon accepted this key.' };
  }
  if (accountId !== undefined && check.accountId !== accountId) {
    return {
      tone: 'negative',
      message: `Alpaca accepted these keys, but they belong to account ${check.accountId}, not ${accountId}. Saving them here will be refused; add ${check.accountId} as an account of its own.`,
    };
  }
  return { tone: 'positive', message: `Alpaca accepted these keys: account ${check.accountId}, ${check.accountStatus}.` };
}

export interface CredentialGroups {
  readonly alpaca: ReadonlyArray<CredentialListing & { readonly credential: AlpacaCredentialSummary }>;
  readonly polygon: ReadonlyArray<CredentialListing & { readonly credential: PolygonCredentialSummary }>;
}

export function groupCredentials(listings: ReadonlyArray<CredentialListing>): CredentialGroups {
  return {
    alpaca: listings.filter((listing): listing is CredentialListing & { readonly credential: AlpacaCredentialSummary } => listing.credential.kind === 'alpaca'),
    polygon: listings.filter((listing): listing is CredentialListing & { readonly credential: PolygonCredentialSummary } => listing.credential.kind === 'polygon'),
  };
}

/** An Alpaca credential as its form holds it. Keys are blank on an edit until replaced. */
export interface AlpacaDraft {
  readonly name: string;
  readonly live: boolean;
  readonly accessKey: string;
  readonly secretKey: string;
  readonly supportData: boolean;
  readonly trackOrders: boolean;
  /** Empty until chosen; the form shows the first of {@link defaultAccountChoices} until then. */
  readonly defaultAccountId: string;
}

export function newAlpacaDraft(): AlpacaDraft {
  return { name: '', live: false, accessKey: '', secretKey: '', supportData: true, trackOrders: true, defaultAccountId: '' };
}

export function alpacaDraftOf(credential: AlpacaCredentialSummary): AlpacaDraft {
  return {
    name: credential.name,
    live: credential.live,
    accessKey: '',
    secretKey: '',
    supportData: credential.supportData,
    trackOrders: credential.trackOrders,
    defaultAccountId: credential.defaultAccountId,
  };
}

/**
 * The accounts a credential's unclaimed orders may go to: those of its own type, as the
 * service insists, active ones first and otherwise oldest first — so a new install's first
 * choice is the account the service created for it.
 */
export function defaultAccountChoices(accounts: ReadonlyArray<Account>, live: boolean): ReadonlyArray<Account> {
  const type = live ? 'live' : 'paper';
  return accounts
    .filter((account) => account.accountType === type)
    .sort((a, b) => Number(b.status === 'active') - Number(a.status === 'active') || a.createdAt - b.createdAt || a.accountId.localeCompare(b.accountId));
}

/**
 * The draft with its default account settled: the one chosen, while it is still a choice,
 * or else the first choice — after the type is switched, or before anything was chosen.
 * Empty when there is no account of the type at all.
 */
export function withDefaultAccount(draft: AlpacaDraft, choices: ReadonlyArray<Account>): AlpacaDraft {
  if (choices.some((account) => account.accountId === draft.defaultAccountId)) {
    return draft;
  }
  return { ...draft, defaultAccountId: choices[0]?.accountId ?? '' };
}

/** An account as a choice reads: its name, its id to tell two of one name apart, and whether it still trades. */
export function accountChoiceLabel(account: Account): string {
  return `${account.name} (${account.accountId})${account.status === 'active' ? '' : ' — inactive'}`;
}

/**
 * What stops the form being sent, or undefined when nothing does. A new credential needs
 * both keys; an edit needs both or neither, since Alpaca issues them as a pair. Either needs
 * a default account.
 */
export function alpacaDraftProblem(draft: AlpacaDraft, editing: boolean): string | undefined {
  if (draft.name.trim().length === 0) {
    return 'Give it a name, such as “Paper” or “Live IRA”.';
  }
  if (draft.defaultAccountId.length === 0) {
    return `Choose the account unclaimed orders go to. Create a ${draft.live ? 'live' : 'paper'} account under Accounts if there is none.`;
  }
  const hasAccess = draft.accessKey.trim().length > 0;
  const hasSecret = draft.secretKey.trim().length > 0;
  if (!editing && (!hasAccess || !hasSecret)) {
    return 'Paste both keys from Alpaca’s dashboard.';
  }
  if (hasAccess !== hasSecret) {
    return 'Replace both keys together, or neither.';
  }
  return undefined;
}

export function alpacaCreateRequest(draft: AlpacaDraft): CreateCredentialRequest {
  return {
    name: draft.name.trim(),
    credential: {
      kind: 'alpaca',
      live: draft.live,
      accessKey: draft.accessKey.trim(),
      secretKey: draft.secretKey.trim(),
      supportData: draft.supportData,
      trackOrders: draft.trackOrders,
      defaultAccountId: draft.defaultAccountId,
    },
  };
}

/** Keys are sent only when both were typed, so a rename never re-sends them. */
export function alpacaUpdateRequest(current: AlpacaCredentialSummary, draft: AlpacaDraft): UpdateCredentialRequest {
  const accessKey = draft.accessKey.trim();
  const secretKey = draft.secretKey.trim();
  return {
    credentialId: current.credentialId,
    version: current.version,
    name: draft.name.trim(),
    credential: {
      kind: 'alpaca',
      live: current.live,
      supportData: draft.supportData,
      trackOrders: draft.trackOrders,
      defaultAccountId: draft.defaultAccountId,
      ...(accessKey.length > 0 && secretKey.length > 0 ? { accessKey, secretKey } : {}),
    },
  };
}

/** The typed keys, to test before saving them; undefined until both are typed. */
export function alpacaTestKeysRequest(draft: AlpacaDraft): TestCredentialKeysRequest | undefined {
  const accessKey = draft.accessKey.trim();
  const secretKey = draft.secretKey.trim();
  return accessKey.length > 0 && secretKey.length > 0 ? { credential: { kind: 'alpaca', live: draft.live, accessKey, secretKey } } : undefined;
}

/** One switch flipped on a row: the stored credential, with that one flag changed. */
export function alpacaToggleRequest(current: AlpacaCredentialSummary, change: { readonly supportData?: boolean; readonly trackOrders?: boolean }): UpdateCredentialRequest {
  return alpacaUpdateRequest(current, { ...alpacaDraftOf(current), ...change });
}

export interface PolygonDraft {
  readonly name: string;
  readonly apiKey: string;
}

export function polygonDraftProblem(draft: PolygonDraft, editing: boolean): string | undefined {
  if (draft.name.trim().length === 0) {
    return 'Give it a name, such as “Polygon”.';
  }
  if (!editing && draft.apiKey.trim().length === 0) {
    return 'Paste the key from Polygon’s dashboard.';
  }
  return undefined;
}

export function polygonTestKeysRequest(draft: PolygonDraft): TestCredentialKeysRequest | undefined {
  const apiKey = draft.apiKey.trim();
  return apiKey.length > 0 ? { credential: { kind: 'polygon', apiKey } } : undefined;
}

export function polygonCreateRequest(draft: PolygonDraft): CreateCredentialRequest {
  return { name: draft.name.trim(), credential: { kind: 'polygon', apiKey: draft.apiKey.trim() } };
}

export function polygonUpdateRequest(current: PolygonCredentialSummary, draft: PolygonDraft): UpdateCredentialRequest {
  const apiKey = draft.apiKey.trim();
  return {
    credentialId: current.credentialId,
    version: current.version,
    name: draft.name.trim(),
    credential: apiKey.length > 0 ? { kind: 'polygon', apiKey } : { kind: 'polygon' },
  };
}

/**
 * What deleting a credential costs, said before it happens, and whether the service will
 * want `force` for it: a live Alpaca credential, which books real fills.
 */
export function deletionWarning(credential: CredentialSummary): { readonly message: string; readonly force: boolean } {
  if (credential.kind === 'polygon') {
    return { message: 'The dividend job will have no Polygon key until another is added.', force: false };
  }
  const feed = credential.trackOrders ? ' Its order feed stops, and fills in that account are no longer booked.' : '';
  if (credential.live) {
    return { message: `These are the keys of LIVE account ${credential.accountId}.${feed} What is already booked stays.`, force: true };
  }
  return { message: `The keys of paper account ${credential.accountId} are forgotten.${feed} What is already booked stays.`, force: false };
}

export interface NotifierDraft {
  readonly name: string;
  readonly description: string;
  readonly channel: string;
  readonly webhookUrl: string;
}

export function newNotifierDraft(): NotifierDraft {
  return { name: '', description: '', channel: '', webhookUrl: '' };
}

export function notifierDraftOf(notifier: Notifier): NotifierDraft {
  return { name: notifier.name, description: notifier.description ?? '', channel: notifier.target.channel, webhookUrl: '' };
}

export function notifierDraftProblem(draft: NotifierDraft, editing: boolean): string | undefined {
  if (draft.name.trim().length === 0) {
    return 'Give it a name. The position monitor posts to “monitor” and “attention”.';
  }
  if (draft.channel.trim().length === 0) {
    return 'Name the channel, such as #fleece-alerts.';
  }
  const url = draft.webhookUrl.trim();
  if (!editing && url.length === 0) {
    return 'Paste the webhook URL from the channel’s Integrations → Webhooks.';
  }
  if (url.length > 0 && discordWebhookId(url) === undefined) {
    return 'That is not a Discord webhook URL. It looks like https://discord.com/api/webhooks/<id>/<token>.';
  }
  return undefined;
}

function notifierFields(draft: NotifierDraft): Omit<CreateNotifierRequest, 'name'> {
  const description = draft.description.trim();
  const webhookUrl = draft.webhookUrl.trim();
  return {
    ...(description.length > 0 ? { description } : {}),
    target: { type: 'discord', channel: draft.channel.trim(), ...(webhookUrl.length > 0 ? { webhookUrl } : {}) },
  };
}

export function notifierCreateRequest(draft: NotifierDraft): CreateNotifierRequest {
  return { name: draft.name.trim(), ...notifierFields(draft) };
}

export function notifierUpdateRequest(current: Notifier, draft: NotifierDraft): UpdateNotifierRequest {
  return { notifierId: current.notifierId, version: current.version, name: draft.name.trim(), ...notifierFields(draft) };
}
